import React, { useState, useRef, useEffect } from "react";
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import PropTypes from 'prop-types';
import { chatItems2 } from "../../utils/data";
import useChatPageData from "../../utils/hooks/useChatPageData";
import Search from "../shared/Search";

const ActiveAI = ({ onSelectChat }) => {
  const { activeChatsList = [], loading } = useChatPageData();
  const chatCardData = chatItems2(activeChatsList);
  // Local chat state for AI chat
  const [messages, setMessages] = useState([
    { sender: "agent", text: "Hello! How can I help you today?" },
  ]);
  const [input, setInput] = useState("");
  const [copyIndex, setCopyIndex] = useState(null);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Always use Vercel API route for both dev and prod
  const API_URL = "/api";

  // Handle message send
  const handleSend = async () => {
    if (!input.trim()) return;
    const userMsg = { sender: "user", text: input };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: input }),
      });
      const data = await res.json();
      // Only log errors, not successful responses to keep console clean
      if (!res.ok) {
        console.error("Error from API:", data);
      }
      setMessages((prev) => [
        ...prev,
        { sender: "agent", text: data.reply || "[No response]" },
      ]);
    } catch (err) {
      console.error(err);
      setMessages((prev) => [
        ...prev,
        { sender: "agent", text: "Error contacting AI backend." },
      ]);
    }
    setSending(false);
  };

  // Handle copy
  const handleCopy = async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyIndex(index);
      setTimeout(() => setCopyIndex(null), 1000);
    } catch (err) {
      console.error('Clipboard copy failed:', err);
    }
  };

  if (loading) return <div className="w-full h-full flex items-center justify-center"><span>Loading...</span></div>;

  return (
    <div className="w-full h-full bg-white flex flex-col overflow-hidden">
      {/* Interaction History Section - Fixed height */}
      <div className="w-full border-b border-[#0000002B] py-1 flex-shrink-0 bg-white">
        <h1 className="pl-2 text-[13px] font-semibold leading-[100%] text-black font-inter">
          Interaction History
        </h1>
      </div>
      <div className="h-[30%] min-h-[150px] overflow-hidden flex flex-col">
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          {chatCardData.map((item, index) => (
          <div
            key={index}
            onClick={() => onSelectChat(item.id)}
            className="flex flex-row items-start w-full gap-1 px-3 cursor-pointer"
          >
            <div className="relative self-start">
              <img
                src={item.image || '/icons/agent-default.png'}
                alt={item.name}
                className="w-[45px] h-[45px] rounded-full object-contain"
                onError={e => { e.target.onerror = null; e.target.src = '/icons/agent-default.png'; }}
              />
              <div
                className={`w-[10px] h-[10px] border-2 border-white rounded-full absolute bottom-0 right-0 ${
                  item.status === "available" ? "bg-[#4DFF00]" : "bg-[#E4E000]"
                }`}
              />
            </div>
            <div className="flex flex-col w-full">
              <div className="flex items-center justify-between w-full">
                <span className="text-[10px] mt-2 font-semibold font-inter leading-[100%] text-black">
                  {item.name}
                </span>
                <span className="text-[9.5px] font-medium font-inter leading-[100%] text-[#00000073] px-2 py-0.5 rounded">
                  {item.time}
                </span>
              </div>
              <div className="text-[9.5px] py-1 font-inter text-[#00000073] text-opacity-80">
                {item.message}
              </div>
            </div>
          </div>
          ))}
        </div>
      </div>
      
      {/* AI Chat Section - Takes remaining height */}
      <div className="w-full flex flex-col border-t border-[#000000B2] flex-1 min-h-0">
        <div className="flex justify-between items-center px-2 py-1.5 flex-shrink-0 bg-white z-10 sticky top-0">
          <h1 className="text-[13px] font-semibold leading-[100%] text-black font-inter">
            AI Assist Agent
          </h1>
        </div>
        <div className="flex flex-col h-full overflow-hidden">
          <div className="flex-1 overflow-y-auto overflow-x-hidden p-2 space-y-2">
            <div className="w-full">
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`w-full pl-2 py-2 flex ${
                    msg.sender === "agent" ? "justify-start" : "justify-end"
                  }`}
                >
                  {/* Wrapper to control relative positioning for chat bubbles */}
                  <div className={`relative max-w-[90%] ${msg.sender === "agent" ? "ml-0" : "ml-auto"}`}>
                    <div
                      className={`px-3 py-2 rounded-[8px] w-full flex items-start break-words ${
                        msg.sender === "agent"
                          ? "bg-[#B6B6B673] text-black text-opacity-80 justify-start"
                          : "border border-[#00000073] border-opacity-[45] bg-white text-black text-opacity-45"
                      }`}
                    >
                      {msg.sender === "agent" ? (
                        <div className="text-[10px] font-inter leading-[130%]">
                          <ReactMarkdown rehypePlugins={[rehypeSanitize]}>{msg.text}</ReactMarkdown>
                        </div>
                      ) : (
                        <span className="text-[10px] font-inter leading-[130%]">{msg.text}</span>
                      )}
                    </div>
                    {/* Copy icon for agent messages only */}
                    {msg.sender === "agent" && (
                      <div>
                        <button
                          type="button"
                          aria-label="Copy message"
                          onClick={() => handleCopy(msg.text, index)}
                          className="w-[20px] h-[20px] absolute -bottom-[6px] right-[-26px] cursor-pointer rounded-[6px] p-1 bg-[#D9D9D9] flex items-center justify-center"
                          tabIndex={0}
                        >
                          <img src="/icons/copy.png" alt="Copy" className="w-full h-full" />
                        </button>
                        {copyIndex === index && (
                          <span className="absolute right-[40px] bottom-[-8px] text-green-500 text-xs bg-white px-1 rounded">Copied!</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          </div>
          {/* Input Bar */}
          <div className="p-2 border-t border-[#0000002B] bg-white flex-shrink-0">
            <Search
              value={input}
              onChange={setInput}
              onSend={handleSend}
              disabled={sending}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

ActiveAI.propTypes = {
  onSelectChat: PropTypes.func.isRequired,
};

export default ActiveAI;
